import type { TransactionChangePayment, TransactionSettlement } from "../../api/specialized.ts";
import type { RefundSource } from "./refundAllocation.ts";

/** Cents, with -0 folded to 0 so a balanced cart never reads "-0.00" or counts as a charge. */
export function cents(n: number): number {
    return Math.round(n * 100) / 100 || 0;
}

/**
 * The cart against customer money in, for the Edit Transaction summary and its settle button.
 *
 * Rounded to cents. Price + Tax summed in floating point leaves dust — a CAPTURE of 110.34 + 6.66
 * and its refund net to -3.6e-15 — and compared exactly with zero, that dust turned a fully
 * refunded, balanced cart into "Save & Charge 0.00".
 *
 * SUBSIDY payments are not customer money: they are an in-house adjustment already represented by
 * the matching RowType 36/42 package row (negative InitialAmount), which `cart` includes. Counting
 * them as paid would double-count and produce a phantom refund. Matches WebAdmin2's
 * WorkingDataSummary (popupEditCart2.ts:369) and the server's RefundAllocation.RefundDue.
 */
export function settlement(cart: number, payments: TransactionChangePayment[]): { cart: number; paid: number; diff: number } {
    const paid = payments
        .filter(p => p.Type !== "SUBSIDY")
        .reduce((s, p) => s + (p.Price ?? 0) + (p.Tax ?? 0), 0);
    const c = cents(cart);
    const pd = cents(paid);
    return { cart: c, paid: pd, diff: cents(c - pd) };
}

export type SettleAction = "save" | "charge" | "refund";

/** What the primary button of Edit Transaction does with the cart's difference. */
export interface SettleIntent {
    action: SettleAction;
    label: string;
    /** What the server will not do and where the operator does it instead; shown under the totals
     *  and in the confirm step. Undefined when the difference is settled in full. */
    note?: string;
}

const money = (n: number) => n.toFixed(2);

/**
 * The primary button for a cart difference, given what the provider can do.
 *
 * The cart is always saved, so the reports stay right when the money moves in the provider's
 * portal. A refund or charge the server cannot make is not offered: the button saves the cart and
 * the note says what to do in the portal (never at the terminal). A refund the refundable payments
 * cover only in part refunds that part. `sources` come from `refundSources` with the server's
 * settlement; without a settlement (older server) every difference is offered as before.
 */
export function settleIntent(diff: number, sources: RefundSource[], s: TransactionSettlement | null | undefined): SettleIntent {
    if (diff > 0) {
        if (s && !s.CanCharge) {
            return {
                action: "save",
                label: "Save Cart",
                note: `${money(diff)} is not charged. ${s.ChargeBlockedReason ?? `${s.ProviderName} cannot charge from the server. Charge the customer in the provider's portal.`}`,
            };
        }
        return { action: "charge", label: `Save & Charge ${money(diff)}` };
    }
    if (diff < 0) {
        const amount = -diff;
        const usable = sources.filter(x => !x.blocked);
        const covered = cents(Math.min(amount, usable.reduce((sum, x) => sum + x.remaining, 0)));
        const reason = sources.find(x => x.blocked)?.blockedReason ?? s?.RefundBlockedReason ?? undefined;
        if (s && covered <= 0) {
            return {
                action: "save",
                label: "Save Cart",
                note: `${money(amount)} is not refunded. ${reason ?? "No payment on this transaction can be refunded from the server. Refund the customer in the provider's portal."}`,
            };
        }
        if (s && covered < amount && reason) {
            return {
                action: "refund",
                label: `Save & Refund ${money(covered)}`,
                note: `${money(cents(amount - covered))} of the ${money(amount)} is not refunded. ${reason}`,
            };
        }
        return { action: "refund", label: `Save & Refund ${money(amount)}` };
    }
    return { action: "save", label: "Save Changes" };
}
